const StockLevel = require('@/models/StockLevel');
const { toInt, toNullableInt } = require('@/functions/transform');

/**
 * Contains an array of objects representing the stock level for each product in the cart and the time that the last
 * check against the API was made.
 *
 * This exists rather than just having the stock level on the cartLine because it is possible to have multiple instances
 * of a product in the cart for example a food item with different options. So this reduces duplicating the same
 * checks as we can store one stock level per branchID/productID.
 *
 * @constructor
 */
const CartProductStockLevels = function () {
    /**
     * @type {StockLevel[]}
     */
    this.stockLevels = [];

    /**
     * When was the last API request to check stock levels?
     *
     * @type {number|null}
     */
    this.stockLastCheckedAt = null;

    /**
     * What is the minimum stock check period in ms if no new products need to be checked?
     *
     * @type {number}
     */
    this.stockCheckInterval = 10000;
};

CartProductStockLevels.prototype = {
    getHydrationMap() {
        return {
            stockLevels: {
                modelCollection: StockLevel,
            },
            stockLastCheckedAt: toNullableInt,
            stockCheckInterval: toInt,
        };
    },

    /**
     * @param {number} branchID
     * @param {number} productID
     * @param {number} availableQty
     */
    setAvailableQty(branchID, productID, availableQty) {
        let stockLevel = this.getOrCreateStockLevel(branchID, productID);
        stockLevel.availableQty = availableQty;
    },

    /**
     * Set that the operator manually disabled (or re-enabled) stock validation for a product.
     *
     * @param {number} branchID
     * @param {number} productID
     * @param {boolean} disableStockValidation
     */
    setDisableStockValidation(branchID, productID, disableStockValidation) {
        let stockLevel = this.getOrCreateStockLevel(branchID, productID);
        stockLevel.disableStockValidation = disableStockValidation;
    },

    /**
     * Check if there is an existing StockLevel object for this branchID+productID.
     * If not, create one then return the object.
     *
     * @param {number} branchID
     * @param {number} productID
     *
     * @returns {StockLevel}
     */
    getOrCreateStockLevel(branchID, productID) {
        // Look for an existing StockLevel.
        let stockLevel = this.getStockLevel(branchID, productID);

        if (!stockLevel) {
            // No existing StockLevel. Create a new one.
            stockLevel = new StockLevel(branchID, productID);

            this.stockLevels.push(stockLevel);
        }

        return stockLevel;
    },

    /**
     * @param {number} branchID
     * @param {number} productID
     *
     * @returns {?StockLevel}
     */
    getStockLevel(branchID, productID) {
        return this.stockLevels.find(
            (stockLevel) => stockLevel.branchID === branchID && stockLevel.productID === productID
        ) || null;
    },

    /**
     * @returns {boolean}
     */
    hasStockLevels() {
        return this.stockLevels.length > 0;
    },

    /**
     * @returns {StockLevel[]}
     */
    getAllStockLevels() {
        return this.stockLevels;
    },

    updateStockLastCheckedAt() {
        this.stockLastCheckedAt = Date.now();
    },

    /**
     * Has more than the stockCheckInterval elapsed since we last did a stock check?
     *
     * @returns {boolean}
     */
    hasStockCheckIntervalElapsed() {
        if (!this.stockLastCheckedAt) {
            return true;
        }
        return Date.now() > (this.stockLastCheckedAt + this.stockCheckInterval);
    },
};

module.exports = CartProductStockLevels;
